/* eslint-disable no-unsafe-optional-chaining */
import { Box } from "@material-ui/core";
import { Checkbox, Grid, MenuItem } from "@mui/material";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import InputLabel from "@mui/material/InputLabel";
import OutlinedInput from "@mui/material/OutlinedInput";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import { useFormik } from "formik";
import React, { useState, useEffect, useMemo, useCallback } from "react";
import Dropzone from "react-dropzone";
import { useDispatch, useSelector } from "react-redux";
import { createSegment } from "../../redux/segments/actions";
import * as Yup from "yup";
import ControlledTreeView from "./PreferencesSet";
import SegmentMap from "./SegmentMap";
import usePreferences from "../../data/usePreferences";
import useRegions from "../../data/useRegions";
import { SEGMENTS_MAIN } from "./consts/segments_main";
import "./CreateSegment.scss";
import { useSnackbar } from "notistack";
import { citiesByRegions } from "../../modules/AdminPanel/Ssp/citiesByRegions";
import SearchIcon from "@mui/icons-material/Search";
import Chip from "@mui/material/Chip";
import { ListSubheader, InputAdornment } from "@mui/material";
import { client } from "../../utils/network";


const GENDER_TYPES = [
  { value: "male", label: "Мужской" },
  { value: "female", label: "Женский" },
  { value: "both", label: "Учитывать оба" },
];

const AGE_MARKER = [
  { value: "0+", label: "0+" },
  { value: "6+", label: "6+" },
  { value: "12+", label: "12+" },
  { value: "16+", label: "16+" },
  { value: "18+", label: "18+" },
];

const FORMIK_INITIAL_VALUES = {
  name: "",
  sex: "",
  age: {
    from: "",
    to: "",
    exclude: [],
    age_restrictions: "",
  },
  regions: [],
  local_geo: [],
  interests: [],
  campaign_list: [],
  search_phrases: {
    phrases: [],
    stop_phrases: [],
  },
  crm: {},
};

const CreateSegmentSchema = Yup.object().shape({
  name: Yup.string(),
  sex: Yup.string().oneOf(GENDER_TYPES.map((gender) => gender.value)),
  age: Yup.object().shape({
    from: Yup.number().min(18).max(75),
    to: Yup.number()
      .min(18)
      .max(75)
      .when("from", (from, field) => (from ? field.min(from) : field)),
    exclude: Yup.array().of(Yup.string().matches(/^[0-9\s-]*$/)),
  }),
  income_level: Yup.object().shape({
    income_level_from: Yup.number().min(0),
    income_level_to: Yup.number()
      .min(0)
      .when("income_level_from", (income_level_from, field) =>
        income_level_from ? field.min(income_level_from) : field
      ),
    income_level_exclude: Yup.array().of(Yup.number().min(0).required()),
  }),
  search_phrases: Yup.object().shape({
    phrases: Yup.array().of(Yup.string().matches(/^[a-zA-Zа-яА-Я0-9\s]*$/)),
    stop_phrases: Yup.array().of(
      Yup.string().matches(/^[a-zA-Zа-яА-Я0-9\s]*$/)
    ),
  }),
});

const ITEM_HEIGHT = 66;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const CityMenuItem = React.memo(({ city, regionName, isSelected, onToggle }) => (
  <MenuItem 
    key={regionName + "_" + city} 
    value={city} 
    onClick={onToggle}
    style={{ padding: "4px 16px", minHeight: "32px", fontSize: 14 }}
  >
    <Checkbox 
      checked={isSelected} 
      size="small"
      style={{ pointerEvents: "none", marginRight: "8px", padding: 0 }} 
    />
    {city}
  </MenuItem>
));

CityMenuItem.displayName = 'CityMenuItem';

const CitiesTreeSelect = ({ value, onChange }) => {
  const [selectedCities, setSelectedCities] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const selectedCitiesRef = React.useRef(selectedCities);
  
  // Обновляем ref при каждом изменении selectedCities
  React.useEffect(() => {
    selectedCitiesRef.current = selectedCities;
  }, [selectedCities]);

  useEffect(() => {
    if (value) {
      const cities = typeof value === 'string' 
        ? value.split(",").map((s) => s.trim()).filter((s) => s)
        : (Array.isArray(value) ? value : []);
      setSelectedCities(cities);
    }
  }, [value]);

  const allCitiesList = useMemo(() => {
    const list = [];
    Object.values(citiesByRegions).forEach((regionObj) => {
      Object.values(regionObj).forEach((cities) => {
        list.push(...cities);
      });
    });
    return list;
  }, []);

  const commit = useCallback((values) => {
    setSelectedCities(values);
    onChange(values);
  }, [onChange]);

  const toggleCity = useCallback((city) => {
    setSelectedCities((prev) => {
      const exists = prev.includes(city);
      const next = exists ? prev.filter((c) => c !== city) : [...prev, city];
      // НЕ вызываем onChange немедленно - только при закрытии dropdown
      return next;
    });
  }, []);

  const handleRemoveCity = useCallback((city) => {
    setSelectedCities((prev) => {
      const next = prev.filter((c) => c !== city);
      // Вызываем onChange для удаления снаружи dropdown
      onChange(next);
      return next;
    });
  }, [onChange]);

  const handleRemoveCityInside = useCallback((city) => {
    setSelectedCities((prev) => prev.filter((c) => c !== city));
  }, []);

  const regionCitiesSelectedState = (cities) => {
    const total = cities.length;
    const selected = cities.filter((c) => selectedCities.includes(c)).length;
    return { total, selected, all: selected === total && total > 0, none: selected === 0, some: selected > 0 && selected < total };
  };

  const toggleRegion = useCallback((cities) => {
    const { all } = regionCitiesSelectedState(cities);
    if (all) {
      const remaining = selectedCities.filter((c) => !cities.includes(c));
      commit(remaining);
    } else {
      const setAll = Array.from(new Set([...selectedCities, ...cities]));
      commit(setAll);
    }
  }, [selectedCities, commit]);

  const selectAllGlobal = useCallback(() => {
    commit(allCitiesList);
  }, [commit, allCitiesList]);

  const deselectAllGlobal = useCallback(() => {
    commit([]);
  }, [commit]);

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return citiesByRegions;
    
    const result = {};
    Object.entries(citiesByRegions).forEach(([fo, regionObj]) => {
      const foMatch = fo.toLowerCase().includes(q);
      Object.entries(regionObj).forEach(([regionName, cities]) => {
        const regionMatch = regionName.toLowerCase().includes(q);
        const matchedCities = foMatch || regionMatch
          ? cities
          : cities.filter((city) => city.toLowerCase().includes(q));
        if (matchedCities.length > 0) {
          if (!result[fo]) result[fo] = {};
          result[fo][regionName] = matchedCities;
        }
      });
    });
    return result;
  }, [searchQuery]);

  const renderGrouped = useMemo(() => {
    if (Object.keys(filtered).length === 0) {
      return (
        <MenuItem disabled style={{ justifyContent: "center", opacity: 0.6, fontSize: 13, padding: "16px" }}>
          Ничего не найдено
        </MenuItem>
      );
    }
    
    return Object.entries(filtered).map(([fo, regionObj]) => (
      <div key={fo}>
        <ListSubheader disableSticky style={{ fontSize: 14, fontWeight: 600, padding: "0px 16px" }}>{fo}</ListSubheader>
        {Object.entries(regionObj).map(([regionName, cities]) => {
          const state = regionCitiesSelectedState(cities);
          return (
            <div key={regionName}>
              <ListSubheader disableSticky style={{ padding: "4px 16px", minHeight: "auto" }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <span style={{ fontSize: 13 }}>{regionName}</span>
                  <Button size="small" onClick={() => toggleRegion(cities)} style={{ fontSize: 11, padding: "2px 6px", minWidth: "auto" }}>
                    {state.all ? "Снять" : "Выбрать все"}
                  </Button>
                </Box>
              </ListSubheader>
              {cities.map((city) => (
                <CityMenuItem
                  key={regionName + "_" + city}
                  city={city}
                  regionName={regionName}
                  isSelected={selectedCities.includes(city)}
                  onToggle={() => toggleCity(city)}
                />
              ))}
            </div>
          );
        })}
      </div>
    ));
  }, [filtered, selectedCities, toggleCity, toggleRegion]);

  return (
    <Box sx={{ width: "60%", padding: "10px 0", textAlign: "start" }}>
      <FormControl fullWidth size="small">
        <Select
          multiple
          displayEmpty
          value={selectedCities}
          renderValue={() => "Выбор городов"}
          style={{ height: 32, background: "#FFFFFF", border: "1px solid #a9a9a9", marginBottom: "5px" }}
          sx={{ "& .css-1d3z3hw-MuiOutlinedInput-notchedOutline": { borderColor: "transparent" } }}
          onOpen={() => {
            setTimeout(() => document.getElementById("search-input-cities-create").focus(), 100);
          }}
          onClose={() => {
            // Синхронизируем выбранные города с formik при закрытии
            onChange(selectedCitiesRef.current);
            setTimeout(() => setSearchQuery(""), 100);
          }}
          MenuProps={{ 
            autoFocus: false, 
            MenuListProps: { 
              autoFocusItem: false,
              style: { maxHeight: "calc(90vh - 100px)", overflow: 'auto', padding: "0px" }
            },
            PaperProps: { 
              style: { maxHeight: "90vh" }
            },
            disablePortal: false,
            variant: "menu"
          }}
        >
          <ListSubheader>
            <Box sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1,
                mb: 1,
                pt: 1,
                maxWidth: "500px",
                maxHeight: "300px",
                overflowY: "auto",
                "&::-webkit-scrollbar": { display: "none" },
                "-ms-overflow-style": "none",
                "scrollbar-width": "none"
              }}>
              {selectedCities.slice(0, 20).map((city) => (
                <Chip
                  key={city}
                  label={city}
                  onDelete={() => handleRemoveCityInside(city)}
                  style={{ border: "2px solid #3f73f994", borderRadius: "15px", margin: "3px", color: "black" }}
                />
              ))}
              {selectedCities.length > 20 && (
                <Chip
                  key="more"
                  label={"+" + (selectedCities.length - 20)}
                  style={{ border: "2px dashed #3f73f994", borderRadius: "15px", margin: "3px", color: "black" }}
                />
              )}
            </Box>
            <Box sx={{ display: "flex", gap: 5, mb: 1, pb: 1 }}>
              <button onClick={selectAllGlobal} style={{ width: "fit-content", backgroundColor: "#dfe8ff", color: "#3f73f9", padding: "5px 10px", fontSize: 14, borderRadius: 5 }}>
                Выбрать все
              </button>
              <button onClick={deselectAllGlobal} style={{ width: "fit-content", backgroundColor: "#dfe8ff", color: "#3f73f9", padding: "5px 10px", fontSize: 14, borderRadius: 5 }}>
                Снять все
              </button>
            </Box>
            <TextField
              size="small"
              placeholder="Поиск города/региона/ФО"
              fullWidth
              autoFocus
              value={searchQuery}
              id="search-input-cities-create"
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              onKeyDown={(e) => {
                if (e.key === "Backspace") {
                  setTimeout(() => document.getElementById("search-input-cities-create").focus(), 100);
                }
                if (e.key !== "Escape") {
                  e.stopPropagation();
                }
              }}
            />
          </ListSubheader>
          {renderGrouped}
        </Select>
      </FormControl>
      {selectedCities.length > 0 && (
        <Box sx={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: 8, 
          mt: 1,
          maxWidth: "100%"
        }}>
          {selectedCities.map((city) => (
            <Chip
              key={city}
              label={city}
              size="medium"
              onDelete={() => handleRemoveCity(city)}
              style={{ 
                border: "2px solid #3f73f994", 
                borderRadius: "16px", 
                height: "32px",
                fontSize: "13px",
                fontWeight: "500",
                color: "black",
                backgroundColor: "#f5f8ff"
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default function CreateSegment(props) {
  const { regions } = useRegions();
  const [isGenderOpen, setIsGenderOpen] = React.useState(false);
  const [isAgeOpen, setIsAgeOpen] = React.useState(false);
  const [isGeoOpen, setIsGeoOpen] = React.useState(false);
  const [isPreferencesOpen, setIsPreferencesOpen] = React.useState(false);
  const [isCampaignOpen, setIsCampaignOpen] = React.useState(false);
  const [isPhrasesOpen, setIsSearchHistoryOpen] = React.useState(false);
  const [users_list_id, setUserList] = React.useState("");
  const { enqueueSnackbar } = useSnackbar();

  const campaigns = useSelector((state) => state.createNewCompany.campaigns);
  const dispatch = useDispatch();
  const { preferences } = usePreferences();

  function nonEmptyObject(obj) {
    const preserveEmptyKeys = new Set(["regions", "local_geo"]);
    for (const propName in obj) {
      if (preserveEmptyKeys.has(propName)) continue;
      const value = obj[propName];
      const isEmptyArray = Array.isArray(value) && value.length === 0;
      if (
        value === null ||
        value === undefined ||
        value === "" ||
        isEmptyArray
      ) {
        delete obj[propName];
      }
    }
    return obj;
  }

  const uploadCrmFile = async (segmentId, file) => {
    if (!segmentId || !file) return;

    const formData = new FormData();
    formData.append("file", file);

    try {
      await client.post("/api/v1/new_segments/users-list", formData, {
        params: { segment_id: segmentId },
      });
    } catch (error) {
      console.error("Ошибка загрузки CRM файла для сегмента", segmentId, error);
      enqueueSnackbar("Не удалось загрузить CRM файл для сегмента", {
        variant: "error",
      });
    }
  };

  const formik = useFormik({
    initialValues: FORMIK_INITIAL_VALUES,
    validationSchema: CreateSegmentSchema,
    onSubmit: async (values) => {
      console.log('Submit triggered with values:', values);
      // Валидация обязательных полей
      const errors = [];
      
      if (!values.name?.trim()) {
        errors.push("Название сегмента");
      }
      if (!values.sex) {
        errors.push("Пол");
      }
      if (!values.age?.from && !values.age?.to && !values.age?.exclude?.length) {
        errors.push("Возраст");
      }
      if (selected === 'regions' && !values.regions?.length) {
        errors.push("Гео регионы");
      } else if (selected === 'maps' && !values.local_geo?.length) {
        errors.push("Локалгео");
      }
      
      console.log('Validation errors:', errors);
      
      if (errors.length > 0) {
        enqueueSnackbar(`Заполните обязательные поля: ${errors.join(', ')}`, { variant: 'error' });
        return;
      }
      
      const finalvalue = {
        name: values?.name,
        sex: [],
        age: {
          from: "",
          to: "",
          exclude: [],
        },
        age_restrictions: "",
        interests: [],
        interests_soc_dem: [],
        // campaign_list: {campaign_ids : []},
        search_phrases: {
          phrases: [],
          stop_phrases: [],
        },
        users_list_id: "",
      };
      // Обработка исключений возраста
      if (values.age.exclude.length !== 0) {
        let arr = [];
        for (var i = 0; i < values.age.exclude.length; i++) {
          let k = values.age.exclude[i].indexOf("-");
          if (k >= 0) {
            let first = parseInt(values.age.exclude[i].substring(k - 2, k));
            let second = parseInt(
              values.age.exclude[i].substring(k + 1, k + 3)
            );
            if (first >= 18 && second <= 75 && second >= first) {
              let j = first;
              while (j <= second) {
                arr.push(j.toString());
                j++;
              }
            }
          } else arr.push(values.age.exclude[i]);
        }
        values.age.exclude = arr;
      }
      
      // Обработка пола
      if (values.sex !== "") {
        values.sex === "both"
          ? (finalvalue.sex = ["male", "female"])
          : (finalvalue.sex = [values?.sex]);
      }
      
      // Обработка возраста
      let flag = true;
      if (
        values.age.from !== "" ||
        values.age.to !== "" ||
        values.age.exclude.length !== 0
      ) {
        finalvalue.age.from = values.age?.from;
        finalvalue.age.to = values.age?.to;
        finalvalue.age.exclude = values.age?.exclude;
      } else {
        delete finalvalue.age;
        flag = false;
      }
      if (values.age?.from === "" && flag === true) {
        delete finalvalue.age.from;
      }
      if (values.age?.to === "" && flag === true) {
        delete finalvalue.age.to;
      }
      if (values.age?.age_restrictions === "") {
        delete finalvalue.age_restrictions;
      } else finalvalue.age_restrictions = values.age?.age_restrictions;
      if (values.age?.exclude.length === 0 && flag === true) {
        delete finalvalue.age.exclude;
      }

      // Геоданные: строго один источник по selected, второй атрибут не отправляем вовсе
      if (selected === "regions") {
        if (Array.isArray(values.regions) && values.regions.length > 0) {
          finalvalue.regions = values.regions;
        }
        delete finalvalue.local_geo;
      } else if (selected === "maps") {
        const locations = Array.isArray(values?.local_geo?.locations)
          ? values.local_geo.locations
          : (Array.isArray(values?.local_geo) ? values.local_geo : []);
        if (locations.length > 0) {
          finalvalue.local_geo = { locations };
        }
        delete finalvalue.regions;
      }
      
      if (values.interests.length !== 0) {
        finalvalue.segments = values.interests;
      }
      
      // Обработка поисковых фраз
      if (
        values.search_phrases.phrases.length !== 0 ||
        values.search_phrases.stop_phrases.length !== 0
      ) {
        finalvalue.search_phrases.phrases = values.search_phrases?.phrases;
        finalvalue.search_phrases.stop_phrases =
          values.search_phrases?.stop_phrases;
      } else {
        delete finalvalue.search_phrases;
      }
      
      // Обработка ID пользователей
      if (!users_list_id) {
        delete finalvalue.users_list_id;
      } else {
        finalvalue.users_list_id = users_list_id;
      }
      
      nonEmptyObject(finalvalue);

      const segmentId = await dispatch(createSegment(finalvalue));

      // после успешного создания сегмента отправляем CRM файл, если он был добавлен
      const crmFile = formik.values?.crm?.file?.file;
      if (segmentId && crmFile) {
        await uploadCrmFile(segmentId, crmFile);
      }

      formik.setValues(FORMIK_INITIAL_VALUES);
      props.onClose();
    },
  });

  function printages(arr) {
    let str = "";
    str += `${arr?.from}-${arr?.to}`;
    //if (arr.exclude?.length !== 0) str += `, исключая ${arr.exclude}`
    return str;
  }

  function printCities(arr) {
    if (!arr || !Array.isArray(arr) || !arr.length) return "";
    return arr.join(", ");
  }

  // Функция для поиска сегмента по IAB коду
  function findSegmentByIabCode(iabCode) {
    for (const segment of SEGMENTS_MAIN) {
      if (segment.iabCode === iabCode) {
        return segment;
      }
      const child = segment.children?.find(child => child.iabCode === iabCode);
      if (child) {
        return child;
      }
    }
    return null;
  }

  function printInterests(arr) {
    // arr содержит IAB коды
    const segments = arr
      .map(iabCode => findSegmentByIabCode(iabCode))
      .filter(Boolean)
      .slice(0, 6);
    let str = "";
    if (!segments.length) return "";
    segments.forEach((el) => (str += `${el.name}, `));
    return str.slice(0, -2);
  }

  // Temp formik context of values and touched
  const [tempFormikValues, setTempFormikValues] = useState(null);
  const [tempFormikTouched, setTempFormikTouched] = useState(null);
  const [selected, setSelected] = useState("regions");
  const [isRegions, setRegions] = React.useState(true);
  const [isMaps, setMaps] = React.useState(false);

  const handleChange = () => {
    console.log(selected);
    if (selected === "regions") {
      setRegions(false);
      setMaps(true);
      console.log("Maps?", formik.values.local_geo);
      //formik.setFieldValue("geo", [])
      //return (<Search value={formik.values.geo} onChange={(value) => formik.setFieldValue("geo", value)}/>)
    } else if (selected === "maps") {
      setMaps(false);
      setRegions(true);
      console.log("Regions", formik.values.regions);
    } //else {
    //  setRegions(false)
    //  setMaps(false)}//formik.setFieldValue("geo", [])
  };

  const saveFormikContext = () => {
    setTempFormikValues(formik.values);
    setTempFormikTouched(formik.touched);
  };
  const restoreFormikContext = () => {
    formik.setValues(tempFormikValues);
    formik.setTouched(tempFormikTouched);
    setTempFormikValues(null);
    setTempFormikTouched(null);
  };
  const clearFormikContext = () => {
    setTempFormikValues(null);
    setTempFormikTouched(null);
  };

  // Gender handlers
  const handleOpenGender = () => {
    saveFormikContext();
    setIsGenderOpen(true);
  };
  const handleCancelGender = () => {
    restoreFormikContext();
    setIsGenderOpen(false);
  };
  const handleApplyGender = () => {
    clearFormikContext();
    setIsGenderOpen(false);
  };
  const handleResetGender = () => {
    clearFormikContext();
    formik.setFieldValue("sex", FORMIK_INITIAL_VALUES.sex);
    setIsGenderOpen(false);
  };

  // age handlers
  const handleOpenAges = () => {
    saveFormikContext();
    setIsAgeOpen(true);
  };
  const handleCancelAges = () => {
    restoreFormikContext();
    setIsAgeOpen(false);
  };
  const handleApplyAges = () => {
    clearFormikContext();
    setIsAgeOpen(false);
  };
  const handleResetAges = () => {
    clearFormikContext();
    formik.setFieldTouched("age", false);
    formik.setFieldValue("age.from", FORMIK_INITIAL_VALUES.age.from);
    formik.setFieldValue("age.to", FORMIK_INITIAL_VALUES.age.to);
    formik.setFieldValue("age.exclude", FORMIK_INITIAL_VALUES.age.exclude);
    formik.setTouched({local_geo:true,regions:true})
    setIsAgeOpen(false);
  };

  // Geo handlers
  const handleOpenGeo = () => {
    saveFormikContext();
    setIsGeoOpen(true);
  };
  const handleCancelGeo = () => {
    restoreFormikContext();
    setIsGeoOpen(false);
    formik.setTouched({local_geo:true,regions:true})
  };
  const handleApplyGeo = () => {
    clearFormikContext();
    setIsGeoOpen(false);
  };
  const handleResetGeo = () => {
    clearFormikContext();
    //formik.setFieldTouched("geo", false);
    formik.setFieldValue("local_geo", FORMIK_INITIAL_VALUES.local_geo);
    formik.setFieldValue("regions", FORMIK_INITIAL_VALUES.regions);
    setIsGeoOpen(false);
  };

  // Preferences handlers
  const handleOpenPreferences = () => {
    saveFormikContext();
    setIsPreferencesOpen(true);
  };
  const handleCancelPreferences = () => {
    restoreFormikContext();
    setIsPreferencesOpen(false);
  };
  const handleApplyPreferences = () => {
    clearFormikContext();
    setIsPreferencesOpen(false);
  };
  const handleResetPreferences = () => {
    clearFormikContext();
    //formik.setFieldTouched("interests", false);
    formik.setFieldValue("interests", FORMIK_INITIAL_VALUES.interests);
    setIsPreferencesOpen(false);
  };

  const validateCsvFile = (file) => {
    return new Promise((resolve, reject) => {
      // Проверяем расширение файла
      if (!file.name.toLowerCase().endsWith('.csv')) {
        reject("Файл должен иметь формат CSV");
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const text = e.target.result;
          const lines = text.split('\n').filter(line => line.trim());
          
          if (lines.length === 0) {
            reject("Файл пустой");
            return;
          }

          const firstLine = lines[0].trim();
          const headers = firstLine.split(/[,;]/).map(h => h.trim().toLowerCase());
          
          if (headers.length < 1 || headers.length > 2) {
            reject("Файл должен содержать 1 или 2 столбца");
            return;
          }

          const validHeaders = ['email', 'phone'];
          const hasValidHeaders = headers.every(header => 
            validHeaders.some(valid => header.includes(valid))
          );

          if (!hasValidHeaders) {
            reject("Первая строка должна содержать заголовок 'email' или 'phone' в каждом столбце");
            return;
          }

          resolve(true);
        } catch (error) {
          reject("Ошибка при чтении файла: " + error.message);
        }
      };

      reader.onerror = () => {
        reject("Ошибка при чтении файла");
      };

      reader.readAsText(file);
    });
  };

  const handleReset = () => {
    formik.setValues(FORMIK_INITIAL_VALUES);
    props.onClose();
  };
  return (
    <Grid
      className="createSegment"
      component="form"
      sx={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Grid
        className="grid"
        container
        spacing={{ xs: 3, md: 3 }}
        columns={{ xs: 2, sm: 8, md: 12 }}
        rowSpacing={5}
        alignItems="center"
        justify="center"
      >
        {/* Name input */}
        <Grid item xs={12}>
          <p
            className="title"
            style={{
              marginBottom: 14,
              height: 24,
              fontWeight: "bold",
              fontSize: 18,
            }}
          >
            Название сегмента
          </p>
          <FormControl sx={{ width: "100%" }}>
            <TextField
              required
              sx={{ minWidth: 120, width: 250 }}
              label="Название"
              name="name"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.name}
              error={formik.errors.name && formik.touched.name}
              input={<OutlinedInput label="Название" />}
            />
          </FormControl>
        </Grid>

        {/* Gender select */}
        <Grid className="gridItem" item xs={3}>
          <p
            className="title"
            style={{
              marginBottom: 14,
              height: 24,
              fontWeight: "bold",
              fontSize: 18,
              textWrap: "nowrap"
            }}
          >
            Социально-демографические
          </p>
          <FormControl sx={{ width: "100%" }}>
            {/* // todo: think how to move label in field when it not done */}
            <TextField
              required
              name="gender"
              onClick={handleOpenGender}
              label="Пол"
              value={
                formik.values.sex
                  ? GENDER_TYPES.find((el) => el.value === formik.values.sex)
                      .label
                  : ""
              }
              onMouseDown={(event) => event.preventDefault()}
              InputProps={{ readOnly: true }}
              id="outlined-multiline-flexible"
              sx={{ minWidth: 120, width: 250 }}
            />
            <Dialog disableEscapeKeyDown open={isGenderOpen}>
              <DialogTitle>Выберите пол</DialogTitle>
              <DialogContent>
                <Box
                  component="form"
                  sx={{ display: "flex", flexWrap: "wrap" }}
                >
                  <FormControl sx={{ m: 2, minWidth: 120, width: 350 }}>
                    <InputLabel id="gender_type-label">Пол</InputLabel>
                    <Select
                      labelId="gender_type-label"
                      id="sex"
                      name="sex"
                      value={formik.values.sex}
                      onChange={formik.handleChange}
                      input={<OutlinedInput label="Пол" />}
                    >
                      {GENDER_TYPES.map((gender_type) => (
                        <MenuItem
                          key={gender_type.value}
                          value={gender_type.value}
                        >
                          {gender_type.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleResetGender}>Сбросить</Button>
                <Button onClick={handleCancelGender}>Отмена</Button>
                <Button onClick={handleApplyGender}>Ok</Button>
              </DialogActions>
            </Dialog>
          </FormControl>
        </Grid>

        {/* age data */}
        <Grid className="socialItem" item xs={3}>
          <p
            className="title"
            style={{
              marginBottom: 14,
              height: 24,
              fontWeight: "bold",
              fontSize: 18,
            }}
          ></p>
          <FormControl sx={{ width: "100%" }}>
            <TextField
              required
              name="age"
              onClick={handleOpenAges}
              onMouseDown={(event) => event.preventDefault()}
              InputProps={{ readOnly: true }}
              label="Возраст"
              value={
                !formik.errors.age &&
                (formik.values.age.from ||
                  formik.values.age.to ||
                  formik.values.age.exclude.length)
                  ? printages(formik.values.age)
                  : ""
              }
              sx={{ minWidth: 120, width: 250 }}
            />
            <Dialog disableEscapeKeyDown open={isAgeOpen}>
              <DialogTitle>Укажите возраст</DialogTitle>
              <DialogContent>
                <Box
                  component="form"
                  sx={{ display: "flex", flexWrap: "wrap", maxWidth: "100%" }}
                >
                  <FormControl sx={{ m: 1, minWidth: 120, width: 350 }}>
                    <TextField
                      fullWidth
                      label="От"
                      value={formik.values.age.from}
                      name="age.from"
                      type="number"
                      placeholder="18"
                      onBlur={formik.handleBlur}
                      onChange={formik.handleChange}
                      sx={{ m: 1 }}
                      input={<OutlinedInput label="Возраст" />}
                      error={
                        formik.errors.age?.from && formik.touched.age?.from
                      }
                    />
                    <TextField
                      fullWidth
                      label="До"
                      type="number"
                      placeholder="75"
                      value={formik.values.age.to}
                      name="age.to"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      sx={{ m: 1 }}
                      input={<OutlinedInput label="Возраст" />}
                      error={formik.errors.age?.to && formik.touched.age?.to}
                    />
                  </FormControl>
                </Box>
                <Box
                  component="form"
                  sx={{ display: "flex", flexWrap: "wrap", maxWidth: "100%" }}
                >
                  <FormControl sx={{ m: 2, minWidth: 120, width: 350 }}>
                    <TextField
                      fullWidth
                      label="Исключить возраст (пример: 20-30, 40)"
                      name="age.exclude"
                      id="fullWidth"
                      value={formik.values.age.exclude.join(",")}
                      onChange={(e) =>
                        formik.setFieldValue(
                          "age.exclude",
                          e.target.value ? e.target.value.split(",") : []
                        )
                      }
                      onBlur={formik.handleBlur}
                      input={
                        <OutlinedInput label="Исключить возраст (пример: 20-30, 40)" />
                      }
                      error={
                        formik.errors.age?.exclude &&
                        formik.touched.age?.exclude
                      }
                    />
                  </FormControl>
                </Box>
                <Box
                  component="form"
                  sx={{ display: "flex", flexWrap: "wrap" }}
                >
                  <FormControl sx={{ m: 2, minWidth: 120, width: 350 }}>
                    <InputLabel id="age_type-label">
                      Возрастная маркировка
                    </InputLabel>
                    <Select
                      labelId="age_type-label"
                      id="age.age_restrictions"
                      name="age.age_restrictions"
                      value={formik.values.age.age_restrictions}
                      onChange={formik.handleChange}
                      input={<OutlinedInput label="Возрастная маркировка" />}
                    >
                      {AGE_MARKER.map((age_type) => (
                        <MenuItem key={age_type.value} value={age_type.value}>
                          {age_type.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleResetAges}>Сбросить</Button>
                <Button onClick={handleCancelAges}>Отмена</Button>
                <Button
                  onClick={handleApplyAges}
                  disabled={Boolean(formik.errors.age)}
                >
                  Ok
                </Button>
              </DialogActions>
            </Dialog>
          </FormControl>
        </Grid>

        {/* Geo data */}
        <Grid className="socialItem" item xs={3}>
          <p
            className="title"
            style={{
              marginBottom: 14,
              height: 24,
              fontWeight: "bold",
              fontSize: 18,
            }}
          ></p>
          <FormControl sx={{ width: "100%" }}>
            <div>
              <TextField
                required
                name="geo"
                value={
                  formik.values.regions.length
                    ? printCities(formik.values.regions)
                    : (formik.values.local_geo.length
                        ? formik.values.local_geo.map(g => g.label).join(', ')
                        : "")
                }
                onClick={handleOpenGeo}
                onMouseDown={(event) => event.preventDefault()}
                InputProps={{ readOnly: true }}
                label="География"
                sx={{
                  minWidth: 120,
                  width: 250,
                  "& .css-1t8l2tu-MuiInputBase-input-MuiOutlinedInput-input ": {
                    textOverflow: "ellipsis",
                  },
                }}
              />
              <Dialog
                disableEscapeKeyDown
                open={isGeoOpen}
                fullWidth
                maxWidth="md"
              >
                <DialogTitle>Укажите гео-данные</DialogTitle>
                <DialogContent>
                  <Box
                    component="form"
                    sx={{ display: "flex", flexWrap: "wrap", maxWidth: "100%", minHeight: 600 }}
                  >
                    <FormControl
                      sx={{
                        m: 2,
                        minWidth: 120,
                        width: "100%",
                        height: 400,
                      }}
                    >
                      <RadioGroup
                        aria-labelledby="demo-radio-buttons-group-label"
                        sx={{ width: "100%" }}
                      >
                        <FormControlLabel
                          value="regions"
                          control={
                            <Radio
                              checked={selected === "regions"}
                              onClick={() => setSelected("regions")}
                              onChange={handleChange}
                              value="regions"
                              name="radio-buttons"
                            />
                          }
                          label="Города"
                        />
                        <FormControlLabel
                          value="maps"
                          control={
                            <Radio
                              // disabled={true}
                              checked={selected === "maps"}
                              onClick={() => setSelected("maps")}
                              onChange={handleChange}
                              value="maps"
                              name="radio-buttons"
                              inputProps={{ "aria-label": "B" }}
                            />
                          }
                          label="Выбрать на карте"
                        />
                      </RadioGroup>
                      {isRegions && (
                        <CitiesTreeSelect
                          value={formik.values.regions}
                          onChange={(value) =>
                            formik.setFieldValue("regions", value)
                          }
                        />
                      )}
                      {isMaps && (
                        <SegmentMap
                          open={isMaps}
                          value={formik.values.local_geo}
                          onChange={(value) =>
                            formik.setFieldValue("local_geo", value)
                          }
                        />
                      )}
                    </FormControl>
                  </Box>
                </DialogContent>
                <DialogActions>
                  <Button onClick={handleResetGeo}>Сбросить</Button>
                  <Button onClick={handleCancelGeo}>Отмена</Button>
                  <Button onClick={handleApplyGeo}>Ok</Button>
                </DialogActions>
              </Dialog>
            </div>
          </FormControl>
        </Grid>

        {/* CRM данные */}
        <Grid className="gridItem" item xs={6} style={{ marginTop: "20px" }}>
          <p
            className="title"
            style={{
              marginBottom: 10,
              height: 24,
              fontWeight: "bold",
              fontSize: 18,
            }}
          >
            CRM данные
          </p>
          <Dropzone
            accept=".csv"
            multiple={false}
            maxSize={10 * 1024 * 1024} // 10 МБ
            onDrop={async (acceptedFiles, rejectedFiles) => {
              if (rejectedFiles && rejectedFiles.length > 0) {
                const rejection = rejectedFiles[0];
                if (rejection.errors.some(e => e.code === 'file-too-large')) {
                  enqueueSnackbar("Размер файла не должен превышать 10 МБ", { variant: 'error' });
                } else if (rejection.errors.some(e => e.code === 'too-many-files')) {
                  enqueueSnackbar("Можно загрузить только 1 файл", { variant: 'error' });
                } else {
                  enqueueSnackbar("Ошибка при загрузке файла", { variant: 'error' });
                }
                return;
              }
              if (acceptedFiles && acceptedFiles.length > 0) {
                const [file] = acceptedFiles;
                try {
                  await validateCsvFile(file);
                  formik.setFieldValue("crm", {
                    file: {
                      file,
                      fileType: "",
                      fileName: file.name,
                    },
                  });
                  enqueueSnackbar("CRM Файл добавлен", { variant: 'success' });
                } catch (error) {
                  enqueueSnackbar(error, { variant: 'error' });
                }
              }
            }}
          >
            {({ getRootProps, getInputProps, isDragAccept, isDragReject }) => (
              <div
                {...getRootProps({
                  style: { 
                    marginTop: 0,
                    backgroundColor: formik.values.crm.file ? "#e8f5e9" : undefined,
                    borderColor: formik.values.crm.file ? "#4caf50" : undefined,
                  },
                  className: `dropzone1 aud
                                    ${isDragAccept && "dropzoneAccept1"}
                                    ${isDragReject && "dropzoneReject1"}
                                    ${formik.values.crm.file && "dropzoneSuccess"}`,
                })}
              >
                <input type="file" {...getInputProps()} />
                <div className="media-plan__drag-container">
                  {formik.values.crm.file ? (
                    <p style={{ textAlign: "center" }}>Файл добавлен</p>
                  ) : (
                    <p style={{ textAlign: "center" }}>Добавить файл</p>
                  )}
                  <p style={{ fontSize: "11px" }} className="media-plan__drag-text">
                  Поддерживается файл формата: csv - используются данные из одного или двух столбцов, первой строчкой каждого столбца должен быть заголовок email или phone
                  </p>
                </div>
              </div>
            )}
          </Dropzone>
        </Grid>

        {/* Preference data */}
        <Grid className="gridItem" item xs={6} style={{ marginBottom: "55px" }}>
          <p
            className="title"
            style={{
              marginBottom: 14,
              height: 24,
              fontWeight: "bold",
              fontSize: 18,
            }}
          >
            Интересы (IAB категории)
          </p>
          <FormControl sx={{ width: "100%" }}>
            <>
              <TextField
                name="interests"
                value={
                  formik.values.interests.length
                    ? printInterests(formik.values.interests)
                    : ""
                }
                onClick={handleOpenPreferences}
                onMouseDown={(event) => event.preventDefault()}
                InputProps={{ readOnly: true }}
                label="Интересы"
                sx={{
                  minWidth: 120,
                  width: 420,
                  "& .css-1t8l2tu-MuiInputBase-input-MuiOutlinedInput-input": {
                    textOverflow: "ellipsis",
                  },
                }}
              />
              <Dialog
                disableEscapeKeyDown
                open={isPreferencesOpen}
                fullWidth
                maxWidth="sm"
              >
                <DialogTitle>Выберите интересы</DialogTitle>
                <DialogContent>
                  <Box
                    component="form"
                    sx={{ display: "flex", flexWrap: "wrap" }}
                  >
                    <FormControl>
                      <ControlledTreeView
                        value={formik.values.interests}
                        onChange={(value) =>
                          formik.setFieldValue("interests", value)
                        }
                      />
                    </FormControl>
                  </Box>
                </DialogContent>
                <DialogActions>
                  <Button onClick={handleResetPreferences}>Сбросить</Button>
                  <Button onClick={handleCancelPreferences}>Отмена</Button>
                  <Button onClick={handleApplyPreferences}>Ok</Button>
                </DialogActions>
              </Dialog>
            </>
          </FormControl>
        </Grid>
      </Grid>

      <Button
        variant="outlined"
        onClick={handleReset}
        style={{ marginTop: 25, marginRight: 15 }}
      >
        Отменить
      </Button>
      <Button
        variant="contained"
        onClick={formik.handleSubmit}
        style={{ marginTop: 25 }}
      >
        Создать
      </Button>
    </Grid>
  );
}
